import { Anchor, Box, BoxProps, Flex } from "@govlaunch/web";
import React from "react";
import Link from "next/link";
import { IListAllCollectionsQuery } from "~/src/components/collection/graphql/__generated__/ListCollectionsQuery.generated";
import { useSelfie } from "~/components/auth/Auth";
import useSignInModal from "~/src/components/auth/useSignInModal";
import CollectionBookmarkButton from "~/src/components/collection/collectionCard/CollectionBookmarkButton";
import CollectionCardName from "~/src/components/collection/collectionCard/CollectionCardName";
import CollectionCardBottomCorner from "~/src/components/collection/collectionCard/CollectionCardBottomCorner";
import CollectionCardForeground from "~/src/components/collection/collectionCard/CollectionCardForeground";
import CollectionCardTotal from "~/src/components/collection/collectionCard/CollectionCardTotal";
import CollectionCardNewContent from "~/src/components/collection/collectionCard/CollectionCardNewContent";
import { ICardViewMode } from "~/src/components/collection/cardViewMode";
import getInitialTabRedirect from "~/src/components/collection/getInitialTabRedirect";

type TCollection = IListAllCollectionsQuery["listAllCollections"]["items"][0];

interface ICollectionCardProps {
  variant?: number | null;
  viewMode?: ICardViewMode;
  collection: TCollection;
  onToggleBookmark?: (collection: TCollection) => void;
}

export default function CollectionCard({
  variant,
  viewMode = ICardViewMode.Singles,
  collection,
  onToggleBookmark,
  ...props
}: ICollectionCardProps & BoxProps) {
  const selfie = useSelfie();
  const signInModal = useSignInModal();

  const totalContentCount =
    collection.contentCounters.groupsCount +
    collection.contentCounters.productsCount +
    collection.contentCounters.projectsCount +
    collection.contentCounters.storiesCount +
    collection.contentCounters.governmentsCount;

  const newContentCount = collection.newContentCounters
    ? collection.newContentCounters.groupsCount +
      collection.newContentCounters.productsCount +
      collection.newContentCounters.projectsCount +
      collection.newContentCounters.storiesCount +
      collection.newContentCounters.governmentsCount
    : 0;

  const redirect = getInitialTabRedirect(collection.contentCounters);

  let linkHref = "/collections/[collectionSlug]/[[...tab]]";
  let linkAs = `/collections/${collection.slug}/projects`;

  if (redirect) {
    linkHref = "/collections/[collectionSlug]/[[...tab]]";
    linkAs = `/collections/${collection.slug}/${redirect}`;
  }

  return (
    <Link href={linkHref} as={linkAs} passHref={true} legacyBehavior={true}>
      <Anchor
        border="none"
        borderColor="transparent"
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={["100%", "100%", "100%", "226px"]}
        height={viewMode === ICardViewMode.Mobile ? "148px" : "224px"}
      >
        <Box
          position="relative"
          width="100%"
          height="100%"
          bg="white"
          color={collection.displayOptions?.cardBackgroundColorHex || "blue.600"}
          border="1px"
          borderColor="gray.100"
          borderRadius="12px 12px 4px 12px"
          boxShadow="lg"
          _hover={{
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
          }}
          overflow="hidden"
          display="flex"
          {...props}
        >
          {onToggleBookmark && (
            <CollectionBookmarkButton
              collection={collection}
              selfie={selfie}
              signInModal={signInModal}
              onToggleBookmark={onToggleBookmark}
            />
          )}
          <CollectionCardForeground variant={variant} />

          <Flex
            position="relative"
            flexDirection="column"
            alignItems="center"
            px={viewMode === ICardViewMode.Mobile ? 2 : 5}
            justifyContent="center"
            flex={1}
          >
            <CollectionCardName viewMode={viewMode} name={collection.name} />
          </Flex>

          {newContentCount > 0 && <CollectionCardNewContent viewMode={viewMode} />}

          <CollectionCardTotal total={totalContentCount} />

          <CollectionCardBottomCorner />
        </Box>
      </Anchor>
    </Link>
  );
}
